import type { FeedbackAnswerQuestionFill, GapQuestionFill } from '@repo/types/questions/QuestionFill';

import { getCorrectTypeColor } from '@repo/lib/quiz/getCorrectTypeColor';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import {
  QuestionMarkedCorrectToggleField,
  QuestionMarkedExplanationField,
  QuestionMarkedFormActions,
  QuestionMarkedPointsField,
  QuestionMarkedResponseCard,
  QuestionMarkedResponseContent,
  QuestionMarkedResponseHeader,
} from '@/components/features/quiz/question/question-marked/QuestionMarked';
import { Form, FormField } from '@/components/ui/form';
import { Markdown } from '@/components/ui/markdown';

import type { QuestionMarkedFillFormProps } from '.';

export function QuestionMarkedFillForm(questionMarkedFillFormProps: QuestionMarkedFillFormProps) {
  const { marked, content, isOpen, onClose, onSubmit: _onSubmit } = questionMarkedFillFormProps;

  const defaultValues = useMemo<FeedbackAnswerQuestionFill>(() => {
    const values: FeedbackAnswerQuestionFill = {};
    content.gaps.forEach((gap) => {
      values[gap.id] = marked?.[gap.id] ?? {
        id: gap.id,
        correct: false,
        points: 0,
        explanation: null,
      };
    });
    return values;
  }, [content, marked]);

  const form = useForm<FeedbackAnswerQuestionFill>({
    defaultValues,
  });

  const onSubmit = (data: FeedbackAnswerQuestionFill) => {
    onClose();
    _onSubmit?.(data);
  };

  useEffect(() => {
    if (isOpen && marked) {
      form.reset(marked);
    }
  }, [isOpen, marked, form]);

  return (
    <Form {...form}>
      <form className="flex w-full flex-col gap-3 p-3" onSubmit={form.handleSubmit(onSubmit)} noValidate>
        <div className="space-y-4">
          {marked &&
            Object.entries(marked).map(([gapId, gap]) => {
              const correctName = `${gapId}.correct` as const;
              const explanationName = `${gapId}.explanation` as const;
              const pointsName = `${gapId}.points` as const;
              const contentGap: GapQuestionFill | undefined = content.gaps.find((g) => g.id === gapId);
              const userAnswer: string | undefined = contentGap?.options.find((o) => o.id === gap?.id)?.content;

              if (!gap) return null;

              return (
                <QuestionMarkedResponseCard key={gapId}>
                  <QuestionMarkedResponseHeader>
                    <QuestionMarkedCorrectToggleField control={form.control} name={correctName} />
                    <QuestionMarkedPointsField control={form.control} name={pointsName} maxPoints={contentGap?.points} />
                  </QuestionMarkedResponseHeader>
                  <QuestionMarkedResponseContent>
                    <FormField
                      name={correctName}
                      control={form.control}
                      render={({ field: { value } }) => (
                        <Markdown content={userAnswer ?? ''} textClassName={getCorrectTypeColor(value)} imageClassName="max-h-68 rounded-sm" videoClassName="max-h-68 rounded-sm" />
                      )}
                    />
                  </QuestionMarkedResponseContent>
                  <QuestionMarkedExplanationField control={form.control} name={explanationName} />
                </QuestionMarkedResponseCard>
              );
            })}
        </div>

        <QuestionMarkedFormActions onClose={onClose} />
      </form>
    </Form>
  );
}
